import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { StableInput, StableTextarea } from '../components/StableInput';
import { Label } from '../components/ui/label';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Plus, 
  Target,
  Users,
  DollarSign,
  Trash2,
  Edit2,
  Loader2,
  BarChart3,
  Activity
} from 'lucide-react';

export default function Programs() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    target_audience: '',
    outcomes: '',
    budget: '',
    beneficiaries_served: '',
    key_activities: '',
    metrics: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchPrograms();
  }, [user, navigate]);

  const fetchPrograms = async () => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setPrograms(data);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      target_audience: '',
      outcomes: '',
      budget: '',
      beneficiaries_served: '',
      key_activities: '',
      metrics: '',
    });
  };

  const handleAddProgram = async () => {
    if (!formData.name || !formData.description) {
      toast.error('Name and description are required');
      return;
    }
    
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          budget: formData.budget ? parseFloat(formData.budget) : null,
          beneficiaries_served: formData.beneficiaries_served ? parseInt(formData.beneficiaries_served) : null,
        }),
      });

      if (response.ok) {
        toast.success('Program added successfully');
        setIsAddDialogOpen(false);
        resetForm();
        fetchPrograms();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add program');
      }
    } catch (error) {
      console.error('Error adding program:', error);
      toast.error('Failed to add program');
    }
  };

  const handleUpdateProgram = async () => {
    if (!selectedProgram) return;
    
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs/${selectedProgram.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          budget: formData.budget ? parseFloat(formData.budget) : null,
          beneficiaries_served: formData.beneficiaries_served ? parseInt(formData.beneficiaries_served) : null,
        }),
      });

      if (response.ok) {
        toast.success('Program updated');
        setIsEditDialogOpen(false);
        setSelectedProgram(null);
        resetForm();
        fetchPrograms();
      }
    } catch (error) {
      console.error('Error updating program:', error);
      toast.error('Failed to update program');
    }
  };

  const handleDeleteProgram = async (programId) => {
    if (!confirm('Are you sure you want to delete this program?')) return;
    
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('Program deleted');
        fetchPrograms();
      }
    } catch (error) {
      console.error('Error deleting program:', error);
    }
  };

  const openEditDialog = (program) => {
    setSelectedProgram(program);
    setFormData({
      name: program.name || '',
      description: program.description || '',
      target_audience: program.target_audience || '',
      outcomes: program.outcomes || '',
      budget: program.budget ? String(program.budget) : '',
      beneficiaries_served: program.beneficiaries_served ? String(program.beneficiaries_served) : '',
      key_activities: program.key_activities || '',
      metrics: program.metrics || '',
    });
    setIsEditDialogOpen(true);
  };

  const ProgramForm = ({ onSubmit, buttonText }) => (
    <div className="space-y-4 mt-4">
      <div>
        <Label>Program Name *</Label>
        <StableInput
          value={formData.name}
          onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="name"
          placeholder="Youth Mentorship Initiative"
          data-testid="program-name-input"
        />
      </div>
      <div>
        <Label>Description *</Label>
        <StableTextarea
          value={formData.description}
          onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="description"
          placeholder="Describe what this program does and its goals..."
          rows={3}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Target Audience</Label>
          <StableInput
            value={formData.target_audience}
            onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="target_audience"
            placeholder="At-risk youth ages 12-18"
          />
        </div>
        <div>
          <Label>Beneficiaries Served</Label>
          <StableInput
            type="number"
            value={formData.beneficiaries_served}
            onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="beneficiaries_served"
            placeholder="500"
          />
        </div>
      </div>
      <div>
        <Label>Key Activities</Label>
        <StableTextarea
          value={formData.key_activities}
          onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="key_activities"
          placeholder="List the main activities of this program..."
          rows={2}
        />
      </div>
      <div>
        <Label>Expected Outcomes</Label>
        <StableTextarea
          value={formData.outcomes}
          onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="outcomes"
          placeholder="What outcomes will this program achieve?"
          rows={2}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Annual Budget ($)</Label>
          <StableInput
            type="number"
            value={formData.budget}
            onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="budget"
            placeholder="50000"
          />
        </div>
        <div>
          <Label>Success Metrics</Label>
          <StableInput
            value={formData.metrics}
            onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="metrics"
            placeholder="e.g., 80% completion rate"
          />
        </div>
      </div>
      <Button onClick={onSubmit} className="w-full bg-primary hover:bg-primary-800 text-white">
        {buttonText}
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-alabaster" data-testid="programs-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold text-primary-900 mb-2">
              Programs
            </h1>
            <p className="text-stone-600">
              Manage your nonprofit's programs for grant writing and proposals
            </p>
          </div>
          
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary-800 text-white" data-testid="add-program-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Program
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Add New Program</DialogTitle>
              </DialogHeader>
              <ProgramForm onSubmit={handleAddProgram} buttonText="Add Program" />
            </DialogContent>
          </Dialog>
        </div>

        {/* Programs List */}
        {programs.length === 0 ? (
          <Card className="bg-white border-stone-200">
            <CardContent className="py-16 text-center">
              <Target className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-stone-700 mb-2">No programs yet</h3>
              <p className="text-stone-500 mb-6">
                Add your organization's programs to use in grant proposals
              </p>
              <Button 
                onClick={() => setIsAddDialogOpen(true)}
                className="bg-primary hover:bg-primary-800 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Program
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {programs.map((program) => (
              <Card key={program.id} className="bg-white border-stone-200 hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{program.name}</CardTitle>
                      {program.target_audience && (
                        <p className="text-sm text-stone-500 mt-1">{program.target_audience}</p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(program)}
                        className="text-stone-500 hover:text-primary"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteProgram(program.id)}
                        className="text-stone-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-stone-600 mb-4 line-clamp-2">
                    {program.description}
                  </p>
                  
                  <div className="grid grid-cols-3 gap-4">
                    {program.beneficiaries_served && (
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                          <Users className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-stone-900">{program.beneficiaries_served.toLocaleString()}</p>
                          <p className="text-xs text-stone-500">Served</p>
                        </div>
                      </div>
                    )}
                    {program.budget && (
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center">
                          <DollarSign className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-stone-900">${(program.budget / 1000).toFixed(0)}k</p>
                          <p className="text-xs text-stone-500">Budget</p>
                        </div>
                      </div>
                    )}
                    {program.metrics && (
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
                          <BarChart3 className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-xs text-stone-600 line-clamp-2">{program.metrics}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {program.outcomes && (
                    <div className="mt-4 p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs font-medium text-stone-500 mb-1">Expected Outcomes:</p>
                      <p className="text-sm text-stone-600 line-clamp-2">{program.outcomes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Program</DialogTitle>
            </DialogHeader>
            <ProgramForm onSubmit={handleUpdateProgram} buttonText="Save Changes" />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
